import { Bookmark, RefreshCw, Trash2 } from 'lucide-react';

import { DEFAULT_PLANNER_TITLE } from '../../constants/schedule';
import { formatDateTime } from '../../utils/date';
import type { StoredPlannerRow } from '../../hooks/schedule/useStoredPlanners';
import IconButton from '../button/IconButton';
import SchedulePanelHeader from './SchedulePanelHeader';

type SavedPlannersPanelProps = {
  planners: StoredPlannerRow[];
  isRefreshing: boolean;
  onRefresh: () => void;
  onOpen: (id: string) => void;
  onCopyOrShare: (item: StoredPlannerRow) => void;
  onRemove: (id: string) => void;
  onClose: () => void;
  className?: string;
};

const SavedPlannersPanel = ({
  planners,
  isRefreshing,
  onRefresh,
  onOpen,
  onCopyOrShare,
  onRemove,
  onClose,
  className = '',
}: SavedPlannersPanelProps) => {
  return (
    <div
      className={`flex flex-col flex-1 min-h-0 h-full overflow-hidden ${className}`}>
      <SchedulePanelHeader
        icon={Bookmark}
        title="Saved on this device"
        onClose={onClose}
        actions={
          <button
            type="button"
            aria-label="Refresh saved planners"
            disabled={isRefreshing}
            onClick={onRefresh}
            className="shrink-0 p-2 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            <RefreshCw
              className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
            />
          </button>
        }
      />

      <div
        className={`flex-1 overflow-y-auto overscroll-y-none hide-scrollbar px-5 py-3 min-h-0 ${
          planners.length === 0 ? 'flex items-center justify-center' : ''
        }`}>
        {planners.length === 0 ? (
          <p className="text-sm text-gray-500 text-center">
            No planners saved. Create one to see it here.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {planners.map((item) => {
              const linkTitle = item.title?.trim() || DEFAULT_PLANNER_TITLE;
              const meta = formatDateTime(item.createdAt);

              return (
                <li
                  key={item.id}
                  className="flex items-center gap-2 py-3 first:pt-0 last:pb-0">
                  <button
                    type="button"
                    onClick={() => onOpen(item.id)}
                    className="flex-1 min-w-0 text-left rounded-lg hover:bg-gray-50 px-1 py-1 -mx-1 transition-colors">
                    <p className="text-base font-bold text-gray-900 truncate">
                      {linkTitle}
                    </p>
                    <p className="text-sm text-gray-500 truncate">{meta}</p>
                  </button>

                  <div className="flex items-center gap-2 shrink-0">
                    <IconButton
                      name="copy"
                      size={18}
                      aria-label={`Copy link for ${linkTitle}`}
                      className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50"
                      onClick={() => onCopyOrShare(item)}
                    />
                    <button
                      type="button"
                      aria-label={`Remove ${linkTitle}`}
                      onClick={() => onRemove(item.id)}
                      className="p-2 rounded-xl border border-gray-200 text-gray-700 hover:bg-red-50 hover:text-red-600 hover:border-red-200">
                      <Trash2 className="h-[18px] w-[18px]" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SavedPlannersPanel;
